import moment from 'moment';
import HelperMixin from '../../Shared/HelperMixin.vue';
import PaginationHelper from '../../Shared/PaginationHelper.vue';

// حالات الطلب
const STATUS_DRAFT = 1;
const STATUS_PENDING = 2;
const STATUS_FROZEN = 3;
const STATUS_ROUTED = 4;
const STATUS_COMPLETED = 5;
const STATUS_REJECTED = 6;
const STATUS_CANCELLED = 7;

const STATUS_OPTIONS = [
    { value: 0, label: 'كل الحالات' },
    { value: STATUS_DRAFT, label: 'مسودة' },
    { value: STATUS_PENDING, label: 'قيد المراجعة' },
    { value: STATUS_FROZEN, label: 'معتمد ومجمَّد' },
    { value: STATUS_ROUTED, label: 'موجَّه لمرفق مستفيد' },
    { value: STATUS_COMPLETED, label: 'مكتمل' },
    { value: STATUS_REJECTED, label: 'مرفوض' },
    { value: STATUS_CANCELLED, label: 'ملغى' },
];

export default {
    name: 'RecycleRequests',
    mixins: [HelperMixin],
    components: { PaginationHelper },

    async created() {
        await this.CheckLoginStatus();
        if (!this.loginDetails || !this.canViewPage) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'واجهة إعادة التدوير متاحة لمستخدمي المرافق الصحية.');
            this.href('/Home');
            return;
        }
        this.GetInfo(1);
    },

    computed: {
        /** أي موظف تابع للمرفق — قرار العميل: بلا صلاحية خاصة. */
        canViewPage() {
            return !!(this.loginDetails && this.isFacilityCenterRole());
        },
        myBranchId() {
            return this.loginDetails ? Number(this.loginDetails.branchesId) : null;
        },
        statusOptions() {
            return STATUS_OPTIONS;
        },
        createTotalQty() {
            return (this.createForm.lines || []).reduce((s, l) => s + Number(l.quantity || 0), 0);
        },
        /** المرشحون غير المضافين بعد إلى الطلب الحالي. */
        availableCandidates() {
            const used = new Set((this.createForm.lines || []).map((l) => l.sourceProductsCountId));
            return this.candidates.filter((c) => !used.has(c.sourceProductsCountId));
        },
    },

    filters: {
        moment(date) {
            if (!date) return '—';
            return moment(date).format('DD/MM/YYYY HH:mm');
        },
        momentDate(date) {
            if (!date) return '—';
            return moment(date).format('DD/MM/YYYY');
        },
    },

    data() {
        return {
            filters: { pageNo: 1, pageSize: 25, status: 0, search: '' },
            Info: [],
            pages: 0,

            detail: null,
            detailDialog: false,

            candidates: [],
            candidatesMeta: { stagnantDays: 90, nearExpiryDays: 90 },
            loadingCandidates: false,

            createDialog: false,
            editingId: null,
            createForm: { note: '', lines: [] },
        };
    },

    methods: {
        // ─── تسميات وحالات ───────────────────────────────────────────────
        statusLabel(s) {
            const found = STATUS_OPTIONS.find((x) => x.value === s);
            return found ? found.label : '—';
        },
        statusBadge(s) {
            if (s === STATUS_COMPLETED) return 'success';
            if (s === STATUS_FROZEN || s === STATUS_ROUTED) return 'primary';
            if (s === STATUS_PENDING) return 'warning';
            if (s === STATUS_REJECTED || s === STATUS_CANCELLED) return 'danger';
            return 'secondary';
        },
        statusBadgeClass(badge) {
            return 'recycle-badge recycle-badge--' + (badge || 'secondary');
        },
        reasonBadge(reason) {
            return reason === 2 ? 'danger' : (reason === 1 ? 'warning' : 'secondary');
        },
        /** هل هذا الطلب وارد إلينا كمرفق مستفيد؟ */
        isIncoming(row) {
            return row && row.beneficiaryBranchesId === this.myBranchId && row.branchesId !== this.myBranchId;
        },

        apiError(err, fallback) {
            const msg = err && err.response && err.response.data ? err.response.data : fallback;
            this.$helper.ShowMessage('error', 'خطأ', msg);
        },

        // ─── القائمة ─────────────────────────────────────────────────────
        GetInfo(pageNo) {
            this.filters.pageNo = pageNo || 1;
            this.$blockUI.Start();
            this.$http.GetRecycleRequests(this.filters)
                .then((res) => {
                    this.$blockUI.Stop();
                    this.Info = (res && res.data && res.data.info) || [];
                    this.pages = (res && res.data && res.data.count) || 0;
                })
                .catch(() => {
                    this.$blockUI.Stop();
                    this.Info = [];
                    this.pages = 0;
                });
        },

        ApplyFilters() {
            this.GetInfo(1);
        },

        ResetFilters() {
            this.filters.status = 0;
            this.filters.search = '';
            this.ApplyFilters();
        },

        // ─── التفاصيل ────────────────────────────────────────────────────
        openDetail(row) {
            if (!row || !row.id) return;
            this.$blockUI.Start();
            this.$http.GetRecycleRequestById(row.id)
                .then((res) => {
                    this.$blockUI.Stop();
                    this.detail = (res && res.data && res.data.info) || null;
                    this.detailDialog = !!this.detail;
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    this.apiError(err, 'تعذّر تحميل تفاصيل الطلب.');
                });
        },

        refreshDetail() {
            if (this.detail) this.openDetail({ id: this.detail.id });
            this.GetInfo(this.filters.pageNo);
        },

        // ─── الأصناف المرشحة ─────────────────────────────────────────────
        loadCandidates() {
            this.loadingCandidates = true;
            return this.$http.GetRecycleCandidates()
                .then((res) => {
                    this.loadingCandidates = false;
                    const d = (res && res.data) || {};
                    this.candidates = d.info || [];
                    this.candidatesMeta = {
                        stagnantDays: d.stagnantDays || 90,
                        nearExpiryDays: d.nearExpiryDays || 90,
                    };
                })
                .catch(() => {
                    this.loadingCandidates = false;
                    this.candidates = [];
                });
        },

        // ─── إنشاء / تعديل ───────────────────────────────────────────────
        openCreate() {
            this.editingId = null;
            this.createForm = { note: '', lines: [] };
            this.createDialog = true;
            this.loadCandidates();
        },

        openEdit(row) {
            this.$blockUI.Start();
            this.$http.GetRecycleRequestById(row.id)
                .then((res) => {
                    this.$blockUI.Stop();
                    const info = (res && res.data && res.data.info) || null;
                    if (!info) return;
                    this.editingId = info.id;
                    this.createForm = {
                        note: info.note || '',
                        lines: (info.lines || [])
                            .filter((l) => l.lineStatus === 1)
                            .map((l) => ({
                                sourceProductsCountId: l.sourceProductsCountId,
                                productName: l.productName,
                                batchNumber: l.batchNumber,
                                expiryDate: l.expiryDate,
                                availableNow: l.availableNow,
                                quantity: l.requestedQuantity,
                                reason: l.reason,
                                note: l.note || '',
                            })),
                    };
                    this.detailDialog = false;
                    this.createDialog = true;
                    this.loadCandidates();
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    this.apiError(err, 'تعذّر تحميل الطلب.');
                });
        },

        addCandidate(c) {
            this.createForm.lines.push({
                sourceProductsCountId: c.sourceProductsCountId,
                productName: c.productName,
                batchNumber: c.batchNumber,
                expiryDate: c.expiryDate,
                availableNow: c.quantity,
                quantity: c.quantity,
                reason: c.reason,
                note: '',
            });
        },

        addAllCandidates() {
            this.availableCandidates.slice().forEach((c) => this.addCandidate(c));
        },

        removeLine(index) {
            this.createForm.lines.splice(index, 1);
        },

        buildPayload() {
            return {
                Note: this.createForm.note || null,
                Lines: (this.createForm.lines || [])
                    .filter((l) => l.sourceProductsCountId && Number(l.quantity) > 0)
                    .map((l) => ({
                        SourceProductsCountId: Number(l.sourceProductsCountId),
                        Quantity: Number(l.quantity),
                        Reason: Number(l.reason || 3),
                        Note: l.note || null,
                    })),
            };
        },

        validateBeforeSave() {
            const lines = this.createForm.lines || [];
            if (!lines.length) return 'أضف صنفاً واحداً على الأقل إلى الطلب.';
            const bad = lines.find((l) => Number(l.quantity) <= 0 || Number(l.quantity) > Number(l.availableNow));
            if (bad) return `الكمية المدخلة للصنف «${bad.productName}» غير صحيحة — الحد الأقصى ${bad.availableNow}.`;
            return null;
        },

        save(saveOnly) {
            const err = this.validateBeforeSave();
            if (err) {
                this.$helper.ShowMessage('warning', 'تنبيه', err);
                return;
            }
            const payload = this.buildPayload();
            this.$blockUI.Start();

            if (this.editingId) {
                this.$http.UpdateRecycleRequest(this.editingId, payload)
                    .then(() => {
                        if (saveOnly) {
                            this.$blockUI.Stop();
                            this.$helper.ShowMessage('success', 'تم', 'تم حفظ التعديلات.');
                            this.createDialog = false;
                            this.GetInfo(this.filters.pageNo);
                            return null;
                        }
                        return this.$http.SubmitRecycleRequest(this.editingId).then((r) => {
                            this.$blockUI.Stop();
                            this.$helper.ShowMessage('success', 'تم', (r.data && r.data.message) || 'تم الإرسال.');
                            this.createDialog = false;
                            this.GetInfo(1);
                        });
                    })
                    .catch((e) => {
                        this.$blockUI.Stop();
                        this.apiError(e, 'تعذّر حفظ الطلب.');
                    });
                return;
            }

            this.$http.CreateRecycleRequest(payload, saveOnly)
                .then((res) => {
                    this.$blockUI.Stop();
                    const data = (res && res.data) || {};
                    this.$helper.ShowMessage('success', 'تم', data.message || 'تم حفظ الطلب.');
                    this.createDialog = false;
                    this.GetInfo(1);
                })
                .catch((e) => {
                    this.$blockUI.Stop();
                    this.apiError(e, 'تعذّر حفظ الطلب.');
                });
        },

        // ─── إجراءات على الطلب ───────────────────────────────────────────
        submitRequest(row) {
            this.$confirm('سيُرسل الطلب إلى إدارة الصيدلة للمراجعة. متابعة؟', 'إرسال الطلب', {
                confirmButtonText: 'إرسال',
                cancelButtonText: 'تراجع',
                type: 'warning',
            }).then(() => {
                this.$blockUI.Start();
                this.$http.SubmitRecycleRequest(row.id)
                    .then((res) => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'تم', (res.data && res.data.message) || 'تم الإرسال.');
                        this.detailDialog = false;
                        this.GetInfo(this.filters.pageNo);
                    })
                    .catch((err) => {
                        this.$blockUI.Stop();
                        this.apiError(err, 'تعذّر إرسال الطلب.');
                    });
            }).catch(() => { });
        },

        cancelRequest(row) {
            this.$prompt('سبب الإلغاء (اختياري)', 'إلغاء الطلب', {
                confirmButtonText: 'إلغاء الطلب',
                cancelButtonText: 'تراجع',
                inputType: 'textarea',
            }).then(({ value }) => {
                this.$blockUI.Start();
                this.$http.CancelRecycleRequest(row.id, { Reason: value || null })
                    .then((res) => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'تم', (res.data && res.data.message) || 'تم الإلغاء.');
                        this.detailDialog = false;
                        this.GetInfo(this.filters.pageNo);
                    })
                    .catch((err) => {
                        this.$blockUI.Stop();
                        this.apiError(err, 'تعذّر إلغاء الطلب.');
                    });
            }).catch(() => { });
        },

        confirmReceipt(row) {
            this.$confirm(
                'بتأكيد الاستلام تُنقل الكميات فعلياً إلى مخزن مرفقكم. لا تؤكد إلا بعد تسلّمها فعلياً. متابعة؟',
                'تأكيد الاستلام',
                { confirmButtonText: 'تأكيد الاستلام', cancelButtonText: 'تراجع', type: 'warning' }
            ).then(() => {
                this.$blockUI.Start();
                this.$http.ConfirmRecycleReceipt(row.id, {})
                    .then((res) => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'تم', (res.data && res.data.message) || 'تم تأكيد الاستلام.');
                        this.detailDialog = false;
                        this.GetInfo(this.filters.pageNo);
                    })
                    .catch((err) => {
                        this.$blockUI.Stop();
                        this.apiError(err, 'تعذّر تأكيد الاستلام.');
                    });
            }).catch(() => { });
        },
    },
};
